import { defineStore } from 'pinia'

export const useUserStore = defineStore('useUserStore', {
    state: () => ({
        user: null,
        permissions: [],
        channel: null,
        role: null,
        isLoaded: false,
        isLoading: false,
        error: null,
    }),

    getters: {
        /**
         * Get the current authenticated user
         */
        currentUser: (state) => state.user,

        /**
         * Check if user is authenticated
         */
        isAuthenticated: (state) => !!state.user,

        /**
         * Get user permissions
         */
        userPermissions: (state) => state.permissions,

        /**
         * Check if user has a specific permission
         */
        hasPermission: (state) => (permission) => {
            // add potential override for cont:upd-org
            if (permission === 'cont:upd-org') {
                if (state.permissions.includes('cont:upd-any')) {
                    return true;
                }
            }

            return state.permissions.includes(permission)
        },

        /**
         * Get user's full name
         */
        userFullName: (state) => {
            if (!state.user) return ''
            if (state.user.name) return state.user.name
            if (state.user.f_name || state.user.l_name) {
                return `${state.user.f_name || ''} ${state.user.l_name || ''}`.trim()
            }
            return state.user.email || ''
        },

        /**
         * Get user's initials
         */
        userInitials: (state) => {
            if (!state.user) return ''
            const name = state.user.name || `${state.user.f_name || ''} ${state.user.l_name || ''}`.trim()
            return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
        },

        /**
         * Get user's profile photo URL
         */
        userProfilePhotoUrl: (state) => {
            if (!state.user) return ''
            return state.user.profile_photo_url || state.user.photo || state.user.img || ''
        },

        /**
         * Get user's title
         */
        userTitle: (state) => {
            if (!state.user) return ''
            return state.user.title || ''
        },

        /**
         * Get user's channel (normalized)
         */
        currentChannel: (state) => {
            return {
                    id: state.channel?.id ?? state.user?.current_organization_id ?? null,
                    name: state.channel?.name ?? null,
                }
        },

        /**
         * Get user's role
         */
        currentRole: (state) => state.role,
    },

    actions: {
        /**
         * Initialize user from API
         * Call this once on app mount
         */
        async init() {
            // Don't fetch if already loaded or currently loading
            if (this.isLoaded || this.isLoading) {
                return
            }

            await this.fetchUser()
        },

        /**
         * Fetch user data from API
         */
        async fetchUser() {
            this.isLoading = true
            this.error = null

            try {
                const response = await axios.get('/api/user_sanctum')

                if (response.data?.success && response.data?.user) {
                    this.user = response.data.user
                    this.permissions = response.data.permissions || []
                    const apiChannel = response.data.channel || null
                    this.channel = apiChannel
                        ? {
                            id: apiChannel.id ?? null,
                            name: apiChannel.name ?? null,
                            companies: Array.isArray(apiChannel.companies) ? apiChannel.companies : [],
                        }
                        : null
                    this.role = response.data.role || null
                    this.isLoaded = true
                }
            } catch (error) {
                console.error('Failed to fetch user from API:', error)
                this.error = error.message || 'Failed to load user data'
            } finally {
                this.isLoading = false
            }
        },

        /**
         * Set user data manually
         */
        setUser(userData) {
            this.user = userData
            this.isLoaded = true
        },

        /**
         * Set permissions
         */
        setPermissions(permissions) {
            this.permissions = permissions
        },

        /**
         * Update specific user properties
         */
        updateUser(updates) {
            if (this.user) {
                this.user = { ...this.user, ...updates }
            }
        },

        /**
         * Refresh user data from API
         */
        async refresh() {
            await this.fetchUser()
        },

        /**
         * Clear user data (logout)
         */
        clearUser() {
            this.user = null
            this.permissions = []
            this.channel = null
            this.role = null
            this.isLoaded = false
            this.error = null
        },
    }
})

